import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError, downloadJson } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Download, CheckCircle2, AlertTriangle, DatabaseBackup } from "lucide-react";
import { ExportButton } from "@/pages/Bills";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

function Row({ label, a, b, ok, testid }) {
  return (
    <div className="grid grid-cols-4 gap-3 py-3 border-t border-neutral-100 items-center" data-testid={testid}>
      <div className="text-sm font-medium">{label}</div>
      <div className="font-mono-num text-right">{inr(a)}</div>
      <div className="font-mono-num text-right">{inr(b)}</div>
      <div className={`text-right text-sm font-semibold ${ok ? "text-[#175C3B]" : "text-[#C4361B]"}`}>
        {ok ? "Match" : `Off by ${inr(Math.abs(a - b))}`}
      </div>
    </div>
  );
}

function Totals({ r }) {
  return (
    <div className="card-surface p-6 mb-6" data-testid="recon-totals">
      <div className="grid grid-cols-4 gap-3 text-[11px] uppercase tracking-widest text-neutral-500 pb-2">
        <div>Figure</div><div className="text-right">Dashboard</div><div className="text-right">Ledger (sum of entries)</div><div className="text-right">Result</div>
      </div>
      <Row label="Received" a={r.dashboard.collected} b={r.ledger.sum_of_payments} ok={r.received_matches} testid="recon-row-received" />
      <Row label="Billed" a={r.dashboard.billed} b={r.ledger.sum_of_bill_amounts} ok={r.billed_matches} testid="recon-row-billed" />
      <Row label="Outstanding" a={r.dashboard.outstanding} b={r.ledger.billed_minus_received}
        ok={r.dashboard.outstanding === r.ledger.billed_minus_received} testid="recon-row-outstanding" />
      <p className="text-xs text-neutral-500 mt-3">
        {r.bills_checked} active bills checked (IST date {r.as_of_ist}). {r.deleted_bills} bills in Recycle Bin are excluded
        ({inr(r.payments_on_deleted_bills)} of payments on them). Orphan payments: {r.orphan_payments} — every payment lives inside its bill.
      </p>
    </div>
  );
}

function Issues({ issues }) {
  if (!issues.length)
    return (
      <div className="card-surface p-6 mb-6 flex items-center gap-3 text-[#175C3B]" data-testid="recon-no-issues">
        <CheckCircle2 size={20} /> No mismatches found.
      </div>
    );
  return (
    <div className="card-surface p-6 mb-6" data-testid="recon-issues">
      <h2 className="font-display text-2xl font-semibold mb-3 flex items-center gap-2">
        <AlertTriangle size={18} className="text-[#C4361B]" /> {issues.length} item(s) to look at
      </h2>
      {issues.map((i, n) => (
        <div key={`${i.bill_id}-${i.type}-${n}`} className="py-2 border-t border-neutral-100 text-sm flex gap-3" data-testid={`recon-issue-${n}`}>
          <span className="chip bg-[#FEE7DF] text-[#C4361B] shrink-0">{i.type.replace(/_/g, " ")}</span>
          <span className="font-mono-num shrink-0">{i.bill_number}</span>
          <span className="text-neutral-600">{i.detail}</span>
        </div>
      ))}
    </div>
  );
}

function Migration() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      setPreview((await api.get("/admin/migration/preview")).data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const apply = async () => {
    const typed = window.prompt("This converts all amounts to paise. A backup is saved first. Type MIGRATE to continue.");
    if (typed !== "MIGRATE") return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/migration/apply", { confirm: typed });
      toast.success(`Migrated ${data.bills_changed} bills. Backup saved first.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card-surface p-6 mb-6" data-testid="migration-panel">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-2xl font-semibold">One-time data conversion</h2>
        <button onClick={load} className="rounded-full border border-neutral-200 px-4 py-2 text-sm hover:bg-neutral-100 transition-colors" data-testid="migration-preview-btn">Show preview</button>
      </div>
      {preview && <MigrationPreview p={preview} apply={apply} busy={busy} />}
    </div>
  );
}

function MigrationPreview({ p, apply, busy }) {
  return (
    <div data-testid="migration-preview">
      <p className="text-sm mb-3">
        {p.bills_to_change} of {p.bills_total} bills will change · {p.payments_to_add} payment(s) added ·{" "}
        {p.duplicates_to_soft_delete} duplicate(s) hidden · {p.flags} flag(s). Old values are kept on each bill.
      </p>
      <div className="max-h-80 overflow-auto text-xs space-y-2 mb-4">
        {p.rows.map((r) => (
          <div key={r.bill_id} className="border-t border-neutral-100 pt-2">
            <span className="font-mono-num font-semibold">{r.bill_number}</span>{r.in_recycle_bin ? " (recycle bin)" : ""}
            <ul className="list-disc ml-5 text-neutral-600">{r.changes.map((c) => <li key={c}>{c}</li>)}</ul>
          </div>
        ))}
      </div>
      {p.bills_to_change > 0 && (
        <button onClick={apply} disabled={busy} className="btn-primary py-2 text-sm" data-testid="migration-apply-btn">
          {busy ? "Converting…" : "Apply conversion"}
        </button>
      )}
    </div>
  );
}

function Backups() {
  const [list, setList] = useState([]);
  const load = useCallback(async () => {
    try {
      setList((await api.get("/admin/backups")).data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const fresh = () => downloadJson("/admin/backup/download", `ledger_backup_${new Date().toISOString().slice(0, 10)}.json`)
    .catch((e) => toast.error(formatApiError(e)));
  const saveNow = async () => {
    try {
      await api.post("/admin/backups");
      toast.success("Backup saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="card-surface p-6" data-testid="backups-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><DatabaseBackup size={18} /> Backups</h2>
        <div className="flex gap-2">
          <button onClick={saveNow} className="rounded-full border border-neutral-200 px-4 py-2 text-sm hover:bg-neutral-100 transition-colors" data-testid="backup-save-btn">Save backup now</button>
          <button onClick={fresh} className="btn-primary py-2 text-sm flex items-center gap-2" data-testid="backup-download-btn">
            <Download size={14} /> Download full backup
          </button>
        </div>
      </div>
      <p className="text-xs text-neutral-500 mb-2">A backup is saved automatically every night at 2am IST (last 30 kept).</p>
      {list.map((b) => (
        <div key={b.id} className="flex justify-between py-2 border-t border-neutral-100 text-sm" data-testid={`backup-row-${b.id}`}>
          <span>{new Date(b.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} · {b.reason} · {b.counts?.bills ?? 0} bills</span>
          <button className="text-forest-600 underline" onClick={() => downloadJson(`/admin/backups/${b.id}`, `ledger_backup_${b.created_at.slice(0, 10)}.json`)}>Download</button>
        </div>
      ))}
    </div>
  );
}

export default function Reconciliation() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = useCallback(async () => {
    setBusy(true);
    try {
      setR((await api.get("/reconciliation")).data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { check(); }, [check]);
  return (
    <div className="p-8 lg:p-14 max-w-[1200px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-[11px] uppercase tracking-[0.35em] text-neutral-500">Owner only</div>
          <h1 className="font-display text-5xl font-bold tracking-tight mt-2">Reconciliation</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportButton />
          <button onClick={check} disabled={busy} className="btn-primary py-3 flex items-center gap-2" data-testid="recon-check-btn">
            <RefreshCw size={16} className={busy ? "animate-spin" : ""} /> Check now
          </button>
        </div>
      </div>
      {r && <Totals r={r} />}
      {r && <Issues issues={r.issues} />}
      <Migration />
      <Backups />
    </div>
  );
}
